import { useState } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Chip,
  Stack,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import useFetchObjects from "../../api/useFetchObjects";
import axios from "axios";
import {
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  LocalMall as LocalMallIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const SaleReturnAdd = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSale, setSelectedSale] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  // Fetch sales with proper error handling
  const {
    data: salesResponse,
    isLoading,
  } = useFetchObjects(
    ["sale", "sale-return-add", searchTerm],
    `shop/sales/?search=${encodeURIComponent(searchTerm)}&page_size=all`,
    token
  );

  // Ensure salesData is always an array
  const salesData = Array.isArray(salesResponse) 
    ? salesResponse 
    : (salesResponse?.results || salesResponse?.data || []);

  const getCurrencyTitle = (sale) => sale?.currency_details?.title || "";

  const getSaleCustomerName = (sale) =>
    sale?.customer_details?.name || "Walk-in Customer";

  const getCurrencyAmount = (amount, sale = selectedSale) => {
    if (amount == null) return 0;
    if (typeof amount === "number") return amount;
    if (typeof amount === "string") return Number(amount) || 0;
    if (typeof amount === "object") {
      const currency = getCurrencyTitle(sale);
      return Number(amount[currency] ?? Object.values(amount)[0] ?? 0) || 0;
    }
    return 0;
  };

  const getSaleSubtotal = (sale) =>
    getCurrencyAmount(sale?.total_amount, sale) ||
    (sale?.sale_items || []).reduce(
      (total, item) =>
        total + (Number(item.quantity) || 0) * (Number(item.sale_price) || 0),
      0
    );

  const getSaleDiscount = (sale) => Number(sale?.discount || 0);

  const getSaleFinalTotal = (sale) => {
    const effectiveTotal = getCurrencyAmount(sale?.effective_total_amount, sale);
    if (effectiveTotal) return effectiveTotal;
    return Math.max(getSaleSubtotal(sale) - getSaleDiscount(sale), 0);
  };

  const calculateRefundAmount = (saleItem, quantity) => {
    const salePrice = Number(saleItem?.sale_price) || 0;
    return (quantity * salePrice).toFixed(2);
  };

  // Handle sale selection
  const handleSaleSelect = (event, sale) => {
    setSelectedSale(sale);
    setReturnItems([]);
  };

  // Toggle item selection with complete validation
  const toggleItemSelection = (item) => {
    if (isSubmitting) return;
    
    setReturnItems((prevItems) => {
      const existingIndex = prevItems.findIndex((i) => i.sale_item === item.id);
      if (existingIndex >= 0) {
        return prevItems.filter((_, index) => index !== existingIndex);
      } else {
        // Stock validation
        const currentStock = item.product_details?.stock_quantity || 0;
        const soldQuantity = item.quantity || 0;
        
        if (soldQuantity <= 0) {
          alert(`Cannot return ${item.product_details?.name || 'this item'} - no items were sold`);
          return prevItems;
        }
        
        const maxReturnable = soldQuantity;
        
        return [
          ...prevItems,
          {
            sale_item: item.id,
            return_quantity: 1,
            refund_amount: calculateRefundAmount(item, 1),
            isCustomRefund: false,
            sale_item_details: item,
            max_returnable: maxReturnable,
            current_stock: currentStock
          },
        ];
      }
    });
  };

  // Handle quantity change with validation
  const handleQuantityChange = (itemId, value) => {
    const returnItem = returnItems.find((item) => item.sale_item === itemId);
    if (!returnItem) return;
    
    const maxReturnable = returnItem.max_returnable || returnItem.sale_item_details?.quantity || 0;
    const quantity = Math.max(0, Math.min(Number(value) || 0, maxReturnable));
    
    if (Number(value) > maxReturnable) {
      alert(`Maximum returnable quantity is ${maxReturnable}`);
    }

    setReturnItems((prevItems) =>
      prevItems.map((item) => {
        if (item.sale_item === itemId) {
          const refund = item.isCustomRefund
            ? item.refund_amount
            : calculateRefundAmount(item.sale_item_details, quantity);
          return {
            ...item,
            return_quantity: quantity,
            refund_amount: refund,
          };
        }
        return item;
      })
    );
  };

  // Handle refund amount change
  const handleRefundChange = (itemId, value) => {
    setReturnItems((prevItems) =>
      prevItems.map((item) => {
        if (item.sale_item === itemId) {
          return {
            ...item,
            refund_amount: Number(value || 0).toFixed(2),
            isCustomRefund: true, // mark as manually overridden
          };
        }
        return item;
      })
    );
  };

  // Calculate total refund amount
  const totalRefundAmount = returnItems
    .reduce((total, item) => total + Number(item.refund_amount || 0), 0)
    .toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSale || returnItems.length === 0 || isSubmitting) {
      return;
    }
    
    // Get current stock and calculate expected result. Sale returns add goods back to stock.
    const stockUpdates = returnItems.map(item => {
      const currentStock = item.current_stock || item.sale_item_details?.product_details?.stock_quantity || 0;
      const returnQty = item.return_quantity;
      const expectedStock = currentStock + returnQty;
      
      return {
        product: item.sale_item_details?.product_details?.name || 'Unknown',
        current: currentStock,
        returned: returnQty,
        expected: expectedStock
      };
    });
    
    console.log('Stock updates expected:', stockUpdates);
    
    setIsSubmitting(true);
    
    const formData = {
      sale: selectedSale.id,
      shop: selectedSale.shop_details?.id,
      reason: reason,
      return_items: returnItems.map((item) => ({
        sale_item: item.sale_item,
        return_quantity: item.return_quantity,
        refund_amount: item.refund_amount,
        is_custom_refund: item.isCustomRefund,
      })),
    };
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/shop/sale-returns/`,
        formData,
        {
          headers: token ? { Authorization: `Token ${token}` } : {},
        }
      );
      
      // Show detailed success message
      const totalReturned = returnItems.reduce((sum, item) => sum + item.return_quantity, 0);
      const totalRefund = returnItems.reduce((sum, item) => sum + parseFloat(item.refund_amount), 0);
      
      let message = `Return created successfully!\n`;
      message += `Items returned: ${totalReturned}\n`;
      message += `Total refund: ${selectedSale?.currency_details?.title || ''} ${totalRefund.toFixed(2)}\n\n`;
      message += `Stock Updates:\n`;
      
      stockUpdates.forEach(update => {
        message += `${update.product}: ${update.current} - ${update.returned} = ${update.expected}\n`;
      });
      
      alert(message);
      navigate("/shop/sale-return");
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to create return';
      alert('Error: ' + errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if an item is selected for return
  const isItemSelected = (itemId) => {
    return returnItems.some((item) => item.sale_item === itemId);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          component={Link}
          to="/shop"
          color="inherit"
          underline="hover"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <LocalMallIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Shop
        </MuiLink>
        <MuiLink
          component={Link}
          to="/shop/sale-return"
          color="inherit"
          underline="hover"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <ReceiptIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Sale Returns
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <AttachMoneyIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          New Return
        </Typography>
      </Breadcrumbs>

      {/* Main Card */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                {t("SaleReturnIndex.form_createTitle")}
              </Typography>
              <Button
                component={Link}
                to="/shop/sale-return"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
              >
                Back to Returns
              </Button>
            </Box>

            <Divider />

            {/* Sale Selection Form */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Sale Search */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={salesData}
                    loading={isLoading}
                    value={selectedSale}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") setSearchTerm(value);
                      if (reason === "clear") setSearchTerm("");
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const customerName = getSaleCustomerName(option);
                      const saleDate = option.sale_date ? new Date(option.sale_date).toLocaleDateString() : 'N/A';
                      const currency = option.currency_details?.title || '';
                      
                      const subtotal = getSaleSubtotal(option).toFixed(2);
                      const totalAmount = getSaleFinalTotal(option).toFixed(2);
                      
                      return `${customerName} - ${saleDate} - Subtotal: ${currency} ${subtotal} - Total: ${currency} ${totalAmount}`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("SaleReturnIndex.form_searchSaleByCustomer", "Search Customer")}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <LocalMallIcon color="action" sx={{ mr: 1 }} />
                          ),
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    onChange={handleSaleSelect}
                    filterOptions={(options) => options}
                    fullWidth
                  />
                </Grid>

                {/* Reason */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("SaleReturnIndex.form_returnReason")}
                    variant="outlined"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InfoIcon color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Sale Details */}
              {selectedSale && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {t("SaleReturnIndex.form_saleInfo")}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                    <Chip
                      label={`${t("SaleReturnIndex.chips_date")}: ${new Date(
                        selectedSale.sale_date
                      ).toLocaleDateString()}`}
                    />
                    <Chip
                      label={`${t("SaleReturnIndex.chips_customer")}: ${
                        getSaleCustomerName(selectedSale)
                      }`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${t("SaleReturnIndex.chips_total")}: ${
                        selectedSale.currency_details?.title || ""
                      } ${
                        getSaleSubtotal(selectedSale).toFixed(2)
                      }`}
                      color="success"
                    />
                    <Chip
                      label={`${t("SaleReturnIndex.chips_discount")}: ${
                        selectedSale.currency_details?.title || ""
                      } ${getSaleDiscount(selectedSale).toFixed(2)}`}
                      color="success"
                    />
                    <Chip
                      label={`${t("SaleReturnIndex.chips_effective")}: ${
                        selectedSale.currency_details?.title || ""
                      } ${
                        getSaleFinalTotal(selectedSale).toFixed(2)
                      }`}
                      color="success"
                    />
                    <Chip
                      label={`${t("SaleReturnIndex.chips_paid")}: ${
                        selectedSale.currency_details?.title || ""
                      } ${
                        getCurrencyAmount(selectedSale.total_paid, selectedSale).toFixed(2)
                      }`}
                      color="success"
                    />

                    <Chip
                      label={`${t("SaleReturnIndex.chips_remained")}: ${
                        selectedSale.currency_details?.title || ""
                      } ${
                        getCurrencyAmount(selectedSale.total_remained_amount, selectedSale).toFixed(2)
                      }`}
                      color="success"
                    />
                  </Stack>
                </Box>
              )}

              {/* Sale Items Table */}
              {selectedSale && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {t("SaleReturnIndex.form_selectItems")}
                  </Typography>
                  <Paper sx={{ overflow: "hidden" }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>
                              {t("SaleReturnIndex.table_product")}
                            </TableCell>
                            <TableCell>
                              {t("SaleReturnIndex.table_unit")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_price")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_soldQty")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_availableQty")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSale.sale_items?.map((item) => (
                            <TableRow
                              key={item.id}
                              hover
                              onClick={() => toggleItemSelection(item)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected(item.id)}
                                />
                              </TableCell>
                              <TableCell>
                                {item.product_details?.name || "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.unit_details?.title || "N/A"}
                              </TableCell>
                              <TableCell align="right">
                                {Number(item.sale_price || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {item.quantity}
                              </TableCell>
                              <TableCell align="right">
                                {item.product_details?.stock_quantity || "N/A"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}

              {/* Selected Items for Return */}
              {returnItems.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {t("SaleReturnIndex.form_returnSummary")}
                  </Typography>
                  <Paper>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {t("SaleReturnIndex.table_product")}
                            </TableCell>
                            <TableCell>
                              {t("SaleReturnIndex.table_unit")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_price")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_returnQty")}
                            </TableCell>
                            <TableCell align="right">
                              {t("SaleReturnIndex.table_refundAmount")}
                            </TableCell>
                            <TableCell align="center">
                              {t("SaleReturnIndex.table_actions")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {returnItems.map((item) => (
                            <TableRow key={item.sale_item}>
                              <TableCell>
                                {item.sale_item_details?.product_details
                                  ?.name || "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.sale_item_details?.unit_details?.title ||
                                  "N/A"}
                              </TableCell>
                              <TableCell align="right">
                                {Number(
                                  item.sale_item_details?.sale_price || 0
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  value={item.return_quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.sale_item,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    min: 1,
                                    max: item.max_returnable || item.sale_item_details?.quantity || 1,
                                  }}
                                  sx={{ width: "100px" }}
                                  helperText={`Max: ${item.max_returnable || item.sale_item_details?.quantity || 1}`}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  value={item.refund_amount}
                                  onChange={(e) =>
                                    handleRefundChange(
                                      item.sale_item,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: "120px" }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  title={t(
                                    "SaleReturnIndex.tooltip_removeItem"
                                  )}
                                >
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      toggleItemSelection(
                                        item.sale_item_details
                                      )
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Divider sx={{ my: 3 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {t("SaleReturnIndex.form_totalRefund")}:{" "}
                        <Box
                          component="span"
                          fontWeight="bold"
                          color="success.main"
                        >
                          {selectedSale?.currency_details?.title || ""}{" "}
                          {totalRefundAmount}
                        </Box>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Stock will be increased by returned quantities
                      </Typography>
                    </Box>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={returnItems.length === 0 || isSubmitting}
                      sx={{ minWidth: 180 }}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <AttachMoneyIcon />
                        )
                      }
                    >
                      {isSubmitting
                        ? `${t("SaleReturnIndex.form_processing")}`
                        : `${t("SaleReturnIndex.form_submit")} & Update Stock`}
                    </Button>
                  </Box>
                </Box>
              )}
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SaleReturnAdd;
